/**
 * Tenant title typography.
 *
 * A tenant picks one Title Style on the Branding page. It is persisted as
 * `theme_config.titleStyle` and reaches the app through TenantContext.
 *
 * This is BRAND TITLE typography, not application typography. It applies only
 * where the tenant's own name is rendered as an identity mark:
 *   - the login screen title
 *   - the sidebar tenant/company title
 *   - the top-bar tenant badge
 * Navigation labels, page headings, tables, forms, dialogs and ordinary
 * Typography keep the application font. Platform and group-owner shells keep
 * fixed platform typography and never consume a tenant's value.
 *
 * The resolver deliberately returns CHARACTER properties only — family, weight,
 * letter-spacing, transform. Callers keep their own layout and font-size rules,
 * so the same style reads correctly at login-hero size and at sidebar size.
 * `sizeScale` is an optical correction (a serif reads small, a light uppercase
 * reads large) offered to callers that opt in by passing their own base size.
 */

export const DEFAULT_TITLE_STYLE = 'modern';

// The app ships no webfont — there is no @font-face, no Google Fonts link and
// no font files — so "Inter" never resolves and the sans stack falls back to
// the OS UI font. Arial carries only Regular and Bold, which means weight 300,
// 600 and 800 all collapse to 400 or 700 and the weight axis largely
// disappears. That is why the four styles read as near-identical at sidebar
// size today.
//
// The styles are therefore separated on axes that render the same everywhere:
// FAMILY, CASING and TRACKING, with weight as a bonus where the platform has
// the cut (Segoe UI ships Light and Semibold on Windows; Helvetica Neue has a
// full range on macOS). Every pair now differs on at least two axes.
//
//            family        casing   tracking   weight
//  modern    UI sans       normal   0.005em    600
//  elegant   serif         normal   0.045em    400
//  bold      Arial Black   UPPER   -0.015em    900
//  minimal   UI sans       UPPER    0.15em     300
//
// All families are core system faces. Inter stays first in the sans stacks so
// the styles improve automatically if it is ever loaded.
const UI_SANS = 'Inter, "Segoe UI", "Helvetica Neue", Arial, sans-serif';

const TITLE_STYLES = {
  modern: {
    fontFamily: UI_SANS,
    fontWeight: 600,
    letterSpacing: '0.005em',
    sizeScale: 1,
  },
  elegant: {
    // Georgia Regular is already the refined cut — asking for a lighter weight
    // would only trigger synthesis, so the character comes from the serif
    // itself plus generous tracking.
    fontFamily: 'Georgia, "Times New Roman", "Book Antiqua", serif',
    fontWeight: 400,
    letterSpacing: '0.045em',
    sizeScale: 1.08,
  },
  bold: {
    // Arial Black is a core system face on Windows and macOS, so this reads as
    // unmistakably heavy even where weight synthesis is unavailable. It is also
    // optically large, hence the reduced scale — a heavier face at the old
    // 1.143 would truncate sidebar names sooner.
    fontFamily: '"Arial Black", "Segoe UI", Inter, "Helvetica Neue", sans-serif',
    fontWeight: 900,
    letterSpacing: '-0.015em',
    textTransform: 'uppercase',
    sizeScale: 1.05,
  },
  minimal: {
    fontFamily: UI_SANS,
    fontWeight: 300,
    // Wide, but deliberately short of extreme: the sidebar title is ellipsized,
    // and the 0.165em gap against `bold` is what separates the two uppercase
    // styles when the weight axis is unavailable.
    letterSpacing: '0.15em',
    textTransform: 'uppercase',
    sizeScale: 0.92,
  },
};

/** The selectable options, in display order, for the Branding page picker. */
export const TITLE_STYLE_OPTIONS = ['modern', 'elegant', 'bold', 'minimal'];

/**
 * Resolve a stored key to a style definition. Missing, null or unknown values
 * fall back to the default rather than throwing, so a malformed
 * `theme_config.titleStyle` can never break the layout.
 */
export const resolveTitleStyle = (titleStyle) =>
  TITLE_STYLES[titleStyle] || TITLE_STYLES[DEFAULT_TITLE_STYLE];

/**
 * Style character for a tenant title, as an MUI `sx` fragment.
 *
 * @param {string} titleStyle  stored key; anything unrecognised uses the default
 * @param {object} [options]
 * @param {number} [options.baseFontSize]  base size in rem. Supply it only when
 *   the caller wants the style's optical size correction applied; omit it and
 *   no fontSize is emitted, leaving the caller's own sizing untouched.
 */
export const getTenantTitleSx = (titleStyle, { baseFontSize } = {}) => {
  const { fontFamily, fontWeight, letterSpacing, textTransform, sizeScale } =
    resolveTitleStyle(titleStyle);

  const sx = { fontFamily, fontWeight, letterSpacing };
  if (textTransform) sx.textTransform = textTransform;
  if (typeof baseFontSize === 'number') {
    sx.fontSize = `${(baseFontSize * sizeScale).toFixed(3)}rem`;
  }
  return sx;
};
